import * as Yup from 'yup';
import { Address } from '../../api/server/models/Address';
import { CoverageType } from '../../api/server/models/CoverageType';
import { PayerContact } from '../../api/server/models/PayerContact';
import { setPaperPath } from '../../services/payer-routing/payer-paper-write';
import { DATA_KINDS, PayerDataKind } from '../../services/payer-routing/payer-routing';

/**
 * What a payer create asks for: the two fields every create demands, and the payer's mailing addresses.
 *
 * `POST /support/payer` and `POST /provider/payer` both require exactly `name` and `coverage`; everything else is
 * optional. Kept in a model file with no React and no `@enter/core` barrel, so the rules run in a Node test.
 */

/** One mailing address as typed: the fields Support's editor asks for, as plain strings. */
export interface IPayerAddressDraft {
    /** The attention line — "Claims Department". Required once the address is started; see `requiredOnceStarted`. */
    name: string;
    line: string;
    city: string;
    /** A US state code, from `US_STATE_OPTIONS`. */
    state: string;
    postal: string;
}

export interface IPayerCreateValues {
    name: string;
    coverage: CoverageType;
    /**
     * Mailing addresses by purpose (FE-87). The claims address is offered from the start; any other purpose is present
     * only once the user adds it, and removing it deletes the key.
     *
     * John Russell, 2026-09-15: *"At least the claims address, but if they want give us the others we shouldnt stop
     * them"*. On the provider console the create is the only chance to send one — there is no `PUT /provider/payer/{id}`.
     */
    addresses: Partial<Record<PayerDataKind, IPayerAddressDraft>>;
}

/** What a create sends. `contact` is absent, not `{}`, when no address was typed. */
export interface IPayerCreatePayload {
    name: string;
    coverage: CoverageType;
    contact?: PayerContact;
}

export const emptyAddressDraft = (): IPayerAddressDraft => ({ name: '', line: '', city: '', state: '', postal: '' });

export const PAYER_CREATE_INITIAL_VALUES: IPayerCreateValues = {
    name: '',
    coverage: CoverageType.COMMERCIALINSURANCECO,
    addresses: { claim: emptyAddressDraft() },
};

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** Anything typed at all: from that point the address has to be complete. */
export function isAddressStarted(draft: IPayerAddressDraft | undefined): boolean {
    return !!draft && Object.values(draft).some(value => text(value) !== '');
}

/**
 * Required once any part of the address is typed.
 *
 * Support's editor rule for a postal path — `isValidPaperValue`: line, city, state and postal code — plus the attention
 * line. The editor leaves `name` out, because whether the API really rejects an address without one was unconfirmed.
 * It does: `POST /provider/payer` answered 400 `{"name": "must not be empty"}` for an address with no attention line
 * (sandbox, 2026-09-15). On the provider console the create is the only write, so that refusal would lose the payer.
 */
const requiredOnceStarted = (message: string) =>
    Yup.string()
        .nullable()
        .test('required-once-started', message, function (value) {
            return !isAddressStarted(this.parent as IPayerAddressDraft) || text(value) !== '';
        });

const addressDraftSchema = Yup.object().shape({
    name: requiredOnceStarted('Attention / department is required'),
    line: requiredOnceStarted('Address line is required'),
    city: requiredOnceStarted('City is required'),
    state: requiredOnceStarted('State is required'),
    postal: requiredOnceStarted('Postal code is required'),
});

export const payerCreateSchema = Yup.object().shape({
    // `trim` first: a name of spaces is no name, and would reach the payer team as a blank row.
    name: Yup.string().trim().required('Name is required'),
    coverage: Yup.mixed<CoverageType>().oneOf(Object.values(CoverageType)).required('Coverage is required'),
    addresses: Yup.object().shape(
        DATA_KINDS.reduce<Record<string, typeof addressDraftSchema>>(
            (shape, kind) => ({ ...shape, [kind]: addressDraftSchema }),
            {},
        ),
    ),
});

/** An address as the API takes it. `country` is left to `setPaperPath`, which defaults it: the API returns 500 without one. */
function toAddress(draft: IPayerAddressDraft): Address {
    return {
        name: text(draft.name),
        lines: [text(draft.line)],
        city: text(draft.city),
        state: text(draft.state),
        postal: text(draft.postal),
    } as unknown as Address;
}

/**
 * The payload, exactly as typed — trimmed, and nothing added.
 *
 * In particular no "Generated Payer" prefix. On the provider console the backend prepends it
 * (@John Russell, 2026-09-11: *"'Generated Payer' does not need to be added in the payload - the
 * backend will add it"*), so adding it here would save "Generated Payer - Generated Payer - …".
 * On the support console it must not be there at all.
 *
 * Addresses become one postal path per purpose, written by `setPaperPath` — the function Support's editor saves
 * with — so an address created here reads back exactly like one set there. A purpose added but left empty sends
 * nothing.
 */
export function toPayerCreatePayload(values: IPayerCreateValues): IPayerCreatePayload {
    const payload: IPayerCreatePayload = { name: values.name.trim(), coverage: values.coverage };

    const contact = DATA_KINDS.reduce<PayerContact | undefined>((built, kind) => {
        const draft = values.addresses?.[kind];
        return draft && isAddressStarted(draft) ? setPaperPath(built, kind, 'Letter', toAddress(draft)) : built;
    }, undefined);

    return contact ? { ...payload, contact } : payload;
}
